import { ReactElement, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { TextField } from '../../../components/TextField';
import { SelectField } from '../../../components/SelectField/SelectField';
import { Button } from '../../../components/Button/Button';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { Routes } from '../../../enums/Routes';
import { useCreateCustomerForPartnerMutation } from '../../../hooks/mutations/useCreateCustomerForPartnerMutation';
import { useGetPartnerDashboardQuery } from '../../../hooks/queries/useGetPartnerDashboardQuery';
import { formatNOK } from '../../../utils/currencyFormatters';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { WalletIcon } from '@navikt/aksel-icons';
import {
  parseOptionalNumber,
  validateBillingStartDate,
  convertNOKToOre,
  calculateCashbackEstimate,
  calculateMonthlyRevenue,
} from '../../../utils/pricing';
import classes from './CreateCustomerPage.module.css';

const PAGE_TITLE = 'Legg til ny kunde';
const BACK_LINK_LABEL = 'Tilbake til kundeoversikt';

const TENANT_NAME_LABEL = 'Kundenavn';
const TENANT_NAME_PLACEHOLDER = 'F.eks. Acme AS';
const ORG_NUMBER_LABEL = 'Organisasjonsnummer';
const ORG_NUMBER_PLACEHOLDER = '123456789';
const ADMIN_EMAIL_LABEL = 'E-post til første admin';
const ADMIN_EMAIL_PLACEHOLDER = 'admin@kunde.no';
const ADMIN_FIRST_NAME_LABEL = 'Fornavn';
const ADMIN_FIRST_NAME_PLACEHOLDER = 'Ola';
const ADMIN_LAST_NAME_LABEL = 'Etternavn';
const ADMIN_LAST_NAME_PLACEHOLDER = 'Nordmann';
const SMS_QUOTA_LABEL = 'SMS-kvote (valgfritt)';
const SMS_QUOTA_PLACEHOLDER = 'Antall SMS, eller la stå tom for ubegrenset';
const AVAILABLE_SEATS_LABEL = 'Antall lisenser (valgfritt)';
const AVAILABLE_SEATS_PLACEHOLDER = 'Antall lisenser, eller la stå tom for ubegrenset';

const PRICE_PER_SEAT_LABEL = 'Pris per lisens i NOK (valgfritt)';
const PRICE_PER_SEAT_PLACEHOLDER = 'F.eks. 299';
const BILLING_PERIOD_LABEL = 'Faktureringsperiode (valgfritt)';
const BILLING_START_DATE_LABEL = 'Startdato for fakturering (valgfritt)';
const CONTRACT_END_DATE_LABEL = 'Sluttdato for avtale (valgfritt)';
const PRICING_SECTION_TITLE = 'Prisinformasjon';
const PRICING_SECTION_DESCRIPTION = 'Dette er valgfri informasjon som kun skal fylles ut dersom kunden har intensjon om å kjøpe og er forespeilet en pris.';

const CREATE_BUTTON_TEXT = 'Opprett kunde';
const CANCEL_BUTTON_TEXT = 'Avbryt';

const SUCCESS_MESSAGE = 'Kunde opprettet! Velkomst-e-post sendt til admin.';
const ERROR_MESSAGE = 'Kunne ikke opprette kunde. Prøv igjen senere.';

const createCustomerSchema = z.object({
  tenantName: z.string().min(1, 'Kundenavn er påkrevd'),
  orgNumber: z.string().min(1, 'Organisasjonsnummer er påkrevd'),
  adminEmail: z
    .string()
    .min(1, 'E-post er påkrevd')
    .email('Ugyldig e-postadresse'),
  adminFirstName: z.string().min(1, 'Fornavn er påkrevd'),
  adminLastName: z.string().min(1, 'Etternavn er påkrevd'),
  smsQuota: z.string().optional(),
  availableSeats: z.string().optional(),
  pricePerSeat: z.string().optional(),
  billingPeriod: z.enum(['', 'monthly', 'yearly']).optional(),
  billingStartDate: z.string().optional(),
  contractEndDate: z.string().optional(),
});

type FormData = z.infer<typeof createCustomerSchema>;

const createEmptyFormData = (): FormData => ({
  tenantName: '',
  orgNumber: '',
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
  smsQuota: '',
  availableSeats: '',
  pricePerSeat: '',
  billingPeriod: '',
  billingStartDate: '',
  contractEndDate: '',
});

type FormErrors = Partial<Record<keyof FormData, string>>;

export const CreateCustomerPage = (): ReactElement => {
  const navigate = useNavigate();
  const createCustomerMutation = useCreateCustomerForPartnerMutation();
  const { data: dashboardData } = useGetPartnerDashboardQuery();

  const [formData, setFormData] = useState<FormData>(createEmptyFormData());
  const [errors, setErrors] = useState<FormErrors>({});

  const cashbackEstimate = useMemo(
    () =>
      calculateCashbackEstimate(
        parseOptionalNumber(formData.pricePerSeat || ''),
        parseOptionalNumber(formData.availableSeats || ''),
        formData.billingPeriod || null,
        dashboardData?.partnerInfo.cashbackPercentage ?? null,
      ),
    [formData.pricePerSeat, formData.availableSeats, formData.billingPeriod, dashboardData],
  );

  const updateFormField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Special validation for billingStartDate
    if (field === 'billingStartDate') {
      const validation = validateBillingStartDate(value);
      if (!validation.isValid) {
        setErrors((prev) => ({ ...prev, [field]: validation.errorMessage }));
        return;
      }
    }

    // Clear error if field had one
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSmsQuotaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      updateFormField('smsQuota', value);
    }
  };

  const handleAvailableSeatsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      updateFormField('availableSeats', value);
    }
  };

  const handlePricePerSeatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty or positive numbers with up to 2 decimals
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      updateFormField('pricePerSeat', value);
    }
  };

  const handleCancel = () => {
    navigate(Routes.PartnerAdminCustomers);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (createCustomerMutation.isPending) {
      return;
    }

    // Validate schema
    const result = createCustomerSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof FormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    // Validate billing start date
    if (formData.billingStartDate) {
      const dateValidation = validateBillingStartDate(formData.billingStartDate);
      if (!dateValidation.isValid) {
        setErrors((prev) => ({ ...prev, billingStartDate: dateValidation.errorMessage }));
        return;
      }
    }

    // Validate contract end date is after billing start date
    if (formData.billingStartDate && formData.contractEndDate) {
      const startDate = new Date(formData.billingStartDate);
      const endDate = new Date(formData.contractEndDate);

      if (endDate <= startDate) {
        setErrors((prev) => ({
          ...prev,
          contractEndDate: 'Sluttdato må være etter startdato for fakturering'
        }));
        return;
      }
    }

    const pricePerSeatNOK = parseOptionalNumber(formData.pricePerSeat || '');
    const pricePerSeatOre = pricePerSeatNOK !== null ? convertNOKToOre(pricePerSeatNOK) : null;

    createCustomerMutation.mutate(
      {
        tenantName: formData.tenantName.trim(),
        orgNumber: formData.orgNumber.trim(),
        adminEmail: formData.adminEmail.trim(),
        adminFirstName: formData.adminFirstName.trim(),
        adminLastName: formData.adminLastName.trim(),
        smsQuota: parseOptionalNumber(formData.smsQuota || ''),
        availableSeats: parseOptionalNumber(formData.availableSeats || ''),
        pricePerSeat: pricePerSeatOre,
        billingPeriod: formData.billingPeriod || null,
        billingStartDate: formData.billingStartDate
          ? new Date(formData.billingStartDate).toISOString()
          : null,
        contractEndDate: formData.contractEndDate
          ? new Date(formData.contractEndDate).toISOString()
          : null,
      },
      {
        onSuccess: () => {
          toast.success(SUCCESS_MESSAGE);
          navigate(Routes.PartnerAdminCustomers);
        },
        onError: () => {
          toast.error(ERROR_MESSAGE);
        },
      }
    );
  };

  return (
    <FocusPageLayout
      title={PAGE_TITLE}
      backLink={Routes.PartnerAdminCustomers}
      backLinkLabel={BACK_LINK_LABEL}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Kundeinformasjon
          </Paragraph>

          <TextField
            name="tenantName"
            label={TENANT_NAME_LABEL}
            value={formData.tenantName}
            onChange={(e) => updateFormField('tenantName', e.target.value)}
            placeholder={TENANT_NAME_PLACEHOLDER}
            type="text"
            required
            errorMessage={errors.tenantName}
          />

          <TextField
            name="orgNumber"
            label={ORG_NUMBER_LABEL}
            value={formData.orgNumber}
            onChange={(e) => updateFormField('orgNumber', e.target.value)}
            placeholder={ORG_NUMBER_PLACEHOLDER}
            type="text"
            required
            errorMessage={errors.orgNumber}
          />
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Admin-bruker
          </Paragraph>
          <Paragraph size="sm" className={classes.sectionDescription}>
            Denne brukeren vil motta en velkomst-e-post med instruksjoner for å sette passord.
          </Paragraph>

          <TextField
            name="adminEmail"
            label={ADMIN_EMAIL_LABEL}
            value={formData.adminEmail}
            onChange={(e) => updateFormField('adminEmail', e.target.value)}
            placeholder={ADMIN_EMAIL_PLACEHOLDER}
            type="email"
            required
            errorMessage={errors.adminEmail}
          />

          <div className={classes.nameFields}>
            <TextField
              name="adminFirstName"
              label={ADMIN_FIRST_NAME_LABEL}
              value={formData.adminFirstName}
              onChange={(e) => updateFormField('adminFirstName', e.target.value)}
              placeholder={ADMIN_FIRST_NAME_PLACEHOLDER}
              type="text"
              required
              errorMessage={errors.adminFirstName}
            />

            <TextField
              name="adminLastName"
              label={ADMIN_LAST_NAME_LABEL}
              value={formData.adminLastName}
              onChange={(e) => updateFormField('adminLastName', e.target.value)}
              placeholder={ADMIN_LAST_NAME_PLACEHOLDER}
              type="text"
              required
              errorMessage={errors.adminLastName}
            />
          </div>
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Abonnement
          </Paragraph>

          <TextField
            name="smsQuota"
            label={SMS_QUOTA_LABEL}
            value={formData.smsQuota}
            onChange={handleSmsQuotaChange}
            placeholder={SMS_QUOTA_PLACEHOLDER}
            type="text"
            errorMessage={errors.smsQuota}
          />

          <TextField
            name="availableSeats"
            label={AVAILABLE_SEATS_LABEL}
            value={formData.availableSeats}
            onChange={handleAvailableSeatsChange}
            placeholder={AVAILABLE_SEATS_PLACEHOLDER}
            type="text"
            errorMessage={errors.availableSeats}
          />
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {PRICING_SECTION_TITLE}
          </Paragraph>
          <Paragraph size="sm" className={classes.sectionDescription}>
            {PRICING_SECTION_DESCRIPTION}
          </Paragraph>

          <TextField
            name="pricePerSeat"
            label={PRICE_PER_SEAT_LABEL}
            value={formData.pricePerSeat}
            onChange={handlePricePerSeatChange}
            placeholder={PRICE_PER_SEAT_PLACEHOLDER}
            type="text"
            errorMessage={errors.pricePerSeat}
          />

          <div className={classes.nameFields}>
            <SelectField
              name="billingPeriod"
              label={BILLING_PERIOD_LABEL}
              value={formData.billingPeriod}
              onChange={(e) => updateFormField('billingPeriod', e.target.value)}
              options={[
                { label: 'Månedlig', value: 'monthly' },
                { label: 'Årlig', value: 'yearly' },
              ]}
              placeholder="Velg periode"
              errorMessage={errors.billingPeriod}
            />

            <TextField
              name="billingStartDate"
              label={BILLING_START_DATE_LABEL}
              value={formData.billingStartDate}
              onChange={(e) => updateFormField('billingStartDate', e.target.value)}
              type="date"
              errorMessage={errors.billingStartDate}
            />
          </div>

          <TextField
            name="contractEndDate"
            label={CONTRACT_END_DATE_LABEL}
            value={formData.contractEndDate}
            onChange={(e) => updateFormField('contractEndDate', e.target.value)}
            type="date"
            errorMessage={errors.contractEndDate}
          />

          {cashbackEstimate && (
            <ContainerCard>
              <ContainerCard.Title icon={<WalletIcon />}>
                Estimert cashback for denne kunden
              </ContainerCard.Title>
              <ContainerCard.Body>
                <Paragraph size="sm" style={{ marginBottom: '1rem', color: 'var(--a-text-subtle)' }}>
                  Estimert cashback er din andel av inntekten fra denne kunden, basert på avtalt cashback-prosent.
                </Paragraph>
                <div className={classes.cashbackBreakdown}>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Pris per lisens</span>
                    <span className={classes.breakdownValue}>
                      {formData.pricePerSeat ? `${formData.pricePerSeat} kr` : '–'}
                    </span>
                  </div>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Antall lisenser</span>
                    <span className={classes.breakdownValue}>
                      {formData.availableSeats || '–'}
                    </span>
                  </div>

                  <div className={classes.breakdownDivider} />

                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}><strong>Total månedssum til sluttkunde</strong></span>
                    <span className={classes.breakdownValue}>
                      <strong>
                        {(() => {
                          const revenue = calculateMonthlyRevenue(
                            parseOptionalNumber(formData.pricePerSeat || ''),
                            parseOptionalNumber(formData.availableSeats || '')
                          );
                          return revenue !== null ? formatNOK(revenue) : '–';
                        })()}
                      </strong>
                    </span>
                  </div>

                  <div className={classes.breakdownHighlight}>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>Cashback-prosent til deg</span>
                      <span className={classes.breakdownValue}>
                        {dashboardData?.partnerInfo.cashbackPercentage !== null && dashboardData?.partnerInfo.cashbackPercentage !== undefined
                          ? `${(dashboardData.partnerInfo.cashbackPercentage / 100).toFixed(1)}%`
                          : 'Ikke satt'}
                      </span>
                    </div>
                  </div>

                  <div className={classes.breakdownHighlight}>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>Månedlig cashback til deg</span>
                      <span className={classes.breakdownValue}>{formatNOK(cashbackEstimate.monthly)}</span>
                    </div>
                  </div>

                  <div className={classes.breakdownHighlight}>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>Årlig cashback til deg</span>
                      <span className={classes.breakdownValue}>{formatNOK(cashbackEstimate.yearly)}</span>
                    </div>
                  </div>
                </div>
              </ContainerCard.Body>
            </ContainerCard>
          )}
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            size="medium"
            onClick={handleCancel}
            disabled={createCustomerMutation.isPending}
            type="button"
          >
            {CANCEL_BUTTON_TEXT}
          </Button>
          <Button
            variant="primary"
            size="medium"
            type="submit"
            disabled={createCustomerMutation.isPending}
          >
            {CREATE_BUTTON_TEXT}
          </Button>
        </div>
      </form>
    </FocusPageLayout>
  );
};
